import type { AxiosError } from 'axios';
import axiosInstance from '../../../lib/axios';
import type { TranslateFileInput, TranslateFileJob } from '../types';

export class TranslateFileApiError extends Error {
  constructor(
    message: string,
    readonly status?: number
  ) {
    super(message);
    this.name = 'TranslateFileApiError';
  }
}

function toApiError(error: unknown): TranslateFileApiError {
  const axiosError = error as AxiosError<{ message?: string; error?: string }>;
  return new TranslateFileApiError(
    axiosError.response?.data?.error || axiosError.response?.data?.message || '',
    axiosError.response?.status
  );
}

export async function createTranslateFileJob(
  input: TranslateFileInput,
  idempotencyKey?: string
): Promise<TranslateFileJob> {
  const formData = new FormData();
  formData.append('file', input.file);
  formData.append('targetLang', JSON.stringify(input.targetLang));
  formData.append('sourceLang', input.sourceLang || 'auto');
  if (input.agentPublicId) formData.append('agentPublicId', input.agentPublicId);

  try {
    const response = await axiosInstance.post('/v1/translate/file/jobs', formData, {
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    });
    return response.data.data;
  } catch (error) {
    throw toApiError(error);
  }
}

export async function getTranslateFileJob(jobId: string): Promise<TranslateFileJob> {
  try {
    const response = await axiosInstance.get(`/v1/translate/file/jobs/${jobId}`);
    return response.data.data;
  } catch (error) {
    throw toApiError(error);
  }
}

export async function cancelTranslateFileJob(jobId: string): Promise<TranslateFileJob> {
  try {
    const response = await axiosInstance.post(`/v1/translate/file/jobs/${jobId}/cancel`);
    return response.data.data;
  } catch (error) {
    throw toApiError(error);
  }
}

export async function fetchTranslationBlob(downloadUrl: string): Promise<Blob> {
  const response = await axiosInstance.get(downloadUrl, { responseType: 'blob' });
  return response.data;
}

export async function downloadTranslationFile(
  downloadUrl: string,
  fileName: string
): Promise<void> {
  let objectUrl: string | null = null;

  try {
    objectUrl = URL.createObjectURL(await fetchTranslationBlob(downloadUrl));

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    if (objectUrl) {
      const url = objectUrl;
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
    }
  }
}
